import type { BillLineItem, POLineDetail } from '../data/types';

export const normCode = (s?: string) => (s || '').trim().toUpperCase();

export function codeMatch(bl: BillLineItem, p: POLineDetail): boolean {
  const pc = normCode(p.inventoryCode);
  return pc !== '' && (normCode(bl.internalCode) === pc || normCode(bl.supplierCode) === pc);
}

export function poLineEx(pl: POLineDetail): number {
  return pl.orderedQty * pl.unitPrice * (1 - (pl.discount || 0) / 100);
}

export interface MatchedPair {
  billLine: BillLineItem;
  poLine: POLineDetail | null;
  qtyMatch: boolean;
  lineDeltaEx: number;
  /** Accepted extra — will be added to PO on sync; PO side is a preview. */
  previewAdd?: boolean;
}

export interface ReconcileBands {
  matched: MatchedPair[];
  extras: BillLineItem[];
  outstanding: POLineDetail[];
}

/** Classify bill vs PO lines into matched / extras / outstanding bands. */
export function buildReconcileBands(
  lineItems: BillLineItem[],
  poLines: POLineDetail[],
  acceptedExtras: Set<number> = new Set(),
): ReconcileBands {
  const matched: MatchedPair[] = [];
  const extras: BillLineItem[] = [];

  for (const bl of lineItems) {
    const pl = poLines.find(p => codeMatch(bl, p));
    if (pl) {
      matched.push({
        billLine: bl,
        poLine: pl,
        qtyMatch: pl.orderedQty === bl.qty,
        lineDeltaEx: bl.lineTotal - poLineEx(pl),
      });
    } else if (acceptedExtras.has(bl.id)) {
      matched.push({
        billLine: bl,
        poLine: null,
        qtyMatch: true,
        lineDeltaEx: 0,
        previewAdd: true,
      });
    } else {
      extras.push(bl);
    }
  }

  const outstanding = poLines.filter(p => !lineItems.some(b => codeMatch(b, p)));

  return { matched, extras, outstanding };
}

export interface SideTotals {
  poSubEx: number;
  poGst: number;
  poTotalInc: number;
  billSubEx: number;
  billGst: number;
  billTotalInc: number;
  /** Bill − PO on ex-GST subtotals — same basis as spine line deltas. */
  differenceEx: number;
}

export function computeSideTotals(
  lineItems: BillLineItem[],
  poLines: POLineDetail[],
): SideTotals {
  const poSubEx = poLines.reduce((s, l) => s + poLineEx(l), 0);
  const billSubEx = lineItems.reduce((s, l) => s + l.lineTotal, 0);
  const billGst = lineItems.reduce((s, l) => s + l.gstAmount, 0);
  const poGst = poSubEx * 0.1;
  const poTotalInc = poSubEx + poGst;
  const billTotalInc = billSubEx + billGst;
  return {
    poSubEx,
    poGst,
    poTotalInc,
    billSubEx,
    billGst,
    billTotalInc,
    differenceEx: billSubEx - poSubEx,
  };
}

export const fmtMoney = (n: number) =>
  `$${n.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export const mulGst = (ex: number, gstMode: 'ex' | 'inc') => (gstMode === 'inc' ? ex * 1.1 : ex);
